import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { Link } from 'react-router-dom';
import { useMst } from '../../core/stores';
import { observer } from 'mobx-react';
import { Typography } from '@material-ui/core';
import { NewOrExisting } from '../../models';

const useStyles = makeStyles(theme => {
  return {
    box: {
      flexShrink: 0,
      boxShadow: theme.shadows[3],
      clipPath: 'inset(0px 0px 0px -15px)',
      minWidth: '20%',
      maxWidth: '28%',
      transition: 'width 0.5s, visibility 0.5s, opacity 0.5s',
      visibility: 'visible',
      opacity: '1',
      [theme.breakpoints.down('sm')]: {
        visibility: 'hidden',
        opacity: '0',
        maxWidth: '0px',
        minWidth: '0px',
      },
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
    },
    listItemText: {
      '& p': {
        whiteSpace: 'pre-wrap',
      },
    },
    titleBar: {
      color: theme.palette.primary.contrastText,
      marginLeft: theme.spacing(2),
    },
    titleContainer: {
      backgroundColor: theme.palette.primary.main,
      boxShadow: theme.shadows[1],
    },
  };
});

const makeSecondaryLineText = text => (text ? `• ${text}\n` : '');

type OrderBreadcrumbsProps = {
  orderId: string;
  className: string;
};

export const OrderBreadcrumbs: React.FC<OrderBreadcrumbsProps> = observer(({ orderId }) => {
  const classes = useStyles();
  const { digitalOrdersStore } = useMst();
  const order = digitalOrdersStore.order(orderId);
  const isVision = import.meta.env.VITE_DO_MODE === 'vision';

  return (
    <Box className={classes.box}>
      <div className={classes.titleContainer}>
        <Typography variant={'subtitle1'} className={classes.titleBar}>
          Your order
        </Typography>
      </div>
      <List>
        {order && order.organization && (
          <ListItem
            button
            key="organization"
            component={Link}
            to={`/digital-orders/${orderId}/organization/`}
          >
            <ListItemText
              primary="Organization"
              secondary={
                order.organization &&
                order.organization.newOrExistingOrganization === NewOrExisting.NEW
                  ? (order.organization as any)?.name || ''
                  : makeSecondaryLineText(
                      digitalOrdersStore.organizationMap.get(
                        (order.organization as any).organizationId,
                      )?.name,
                    )
              }
            />
          </ListItem>
        )}
        {(order && order.facility && order.facility.facilities.length && (
          <ListItem
            button
            key="facilities"
            component={Link}
            to={`/digital-orders/${orderId}/facilities/`}
          >
            <ListItemText
              className={classes.listItemText}
              primary="Facilities"
              secondary={order.facility.facilities
                .map((f: any) => {
                  const label = f.facilityId
                    ? digitalOrdersStore.facilityMap.get(f.facilityId).name
                    : f.name;
                  return makeSecondaryLineText(label);
                })
                .sort()}
            />
          </ListItem>
        )) ||
          null}
        {order && order.products && (
          <ListItem
            button
            key="products"
            component={Link}
            to={`/digital-orders/${orderId}/products/`}
          >
            <ListItemText
              className={classes.listItemText}
              primary="Products"
              secondary={Object.keys(order.products.productSelection)
                .filter(i => !!order.products.productSelection[i])
                .map(prod => {
                  const product = prod === 'AM_VISION' ? 'Asset Management' : prod;
                  return makeSecondaryLineText(product);
                })}
            />
          </ListItem>
        )}
        {!isVision && order && order.products && (
          <ListItem
            button
            key="integrations"
            component={Link}
            to={`/digital-orders/${orderId}/products/`}
          >
            <ListItemText
              className={classes.listItemText}
              primary="Integrations"
              secondary={Object.keys(order.products.integrations)
                .filter(i => !!order.products.integrations[i])
                .map(i => {
                  switch (i) {
                    case 'other':
                      return `Other: ${order.productDetails.other.integrationName}`;
                    case 'ECOFIT':
                      return 'Ecofit';
                    default:
                      return i;
                  }
                })
                .map(makeSecondaryLineText)}
            />
          </ListItem>
        )}
      </List>
    </Box>
  );
});
